import Big from 'big.js';
import * as buttons from 'smg/buttons';
import { GetChildren, GetSizes } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import { NewQuoteRequest } from 'smg/customer-api/quoterequests';
import { Session } from 'smg/customer-api/session';
import { ShowError } from 'smg/errors';
import { BlockPastDates, ReportValidity } from 'smg/form';
import * as html from 'smg/html';
import { Phone } from 'smg/phone';
import { Uploader } from 'smg/uploader/v2';

declare interface myWindow extends Window {
    readonly lineItem: string;
}
declare const window: myWindow;

const LINE_ITEM = window.lineItem;

// Clone a pre-rendered <template> by its data-file attribute (Hugo img partial).
// Matched on the extension-less path: resources.Match names carry no leading
// slash, and the upgrade animations ship as video whose container varies.
function cloneImageTemplate(file: string): HTMLElement | null {
    const stem = file.replace(/^\//, '').replace(/\.[^./]+$/, '');
    const tpl = document.querySelector<HTMLTemplateElement>(
        `.quote-image-template[data-file^="${stem}."], .quote-image-template[data-file^="/${stem}."]`,
    );
    if (!tpl) {
        return null;
    }
    return tpl.content.firstElementChild?.cloneNode(true) as HTMLElement | null;
}

const PLATING_IMAGES: Record<string, string> = {
    'Antique Copper': '/img/free-quote/plating/antique-copper.jpg',
    'Antique Gold': '/img/free-quote/plating/antique-gold.jpg',
    'Antique Silver': '/img/free-quote/plating/antique-silver.jpg',
    'Black Metal': '/img/free-quote/plating/black-metal.jpg',
    'Copper': '/img/free-quote/plating/copper.jpg',
    'Gold': '/img/free-quote/plating/gold.jpg',
    'Silver': '/img/free-quote/plating/silver.jpg',
};

const UPGRADE_IMAGES: Record<string, string> = {
    'Dangler': '/img/free-quote/upgrades/dangler.mp4',
    'Spinner': '/img/free-quote/upgrades/spinner.mp4',
    'Blinker': '/img/free-quote/upgrades/blinker.mp4',
    'Bobble': '/img/free-quote/upgrades/bobble.mp4',
    'Slider': '/img/free-quote/upgrades/slider.mp4',
    'Glitter Color Fill': '/img/free-quote/upgrades/glitter.mp4',
};

const CARD_TYPES: Record<string, string> = {
    'tpl-trading-1': 'Trading Pins',
    'tpl-trading-2': 'Trading Pins',
    'tpl-trading-3': 'Trading Pins',
    'tpl-trading-4': 'Trading Pins',
    'tpl-trading-5': 'Trading Pins',
    'tpl-custom': 'Trading Pins',
    'tpl-rush': 'Rush Pins',
};

void main();

async function main() {
    const $form = document.querySelector('#quote-form') as HTMLFormElement;
    const $fieldset = $form.querySelector('fieldset') as HTMLFieldSetElement;
    const $templateCards = document.querySelectorAll<HTMLElement>('#step-template .quote-card');
    const $steps = document.querySelector('#quote-steps') as HTMLElement;
    const $info = document.querySelector('#quote-info') as HTMLElement;
    const $quantity = $info.querySelector('#quantity') as HTMLInputElement;
    const $projectName = $info.querySelector('#project-name') as HTMLInputElement;
    const $budget = $info.querySelector('#budget') as HTMLInputElement;
    const $deadlineRadios = Array.from($info.querySelectorAll('input[name="deadline-radio"]')) as HTMLInputElement[];
    const $dateNeededContainer = $info.querySelector('#date-needed-container') as HTMLElement;
    const $dateNeeded = $dateNeededContainer.querySelector('#date-needed') as HTMLInputElement;
    const revalidateDateNeeded = BlockPastDates($dateNeeded, { earliest: 'tomorrow' });
    const $uploads = $info.querySelector('#uploads') as HTMLLabelElement;
    const $sport = $info.querySelector('#sport') as HTMLInputElement;
    const $teamColors = $info.querySelector('#team-colors') as HTMLInputElement;
    const $projectNotes = $info.querySelector('#project-notes') as HTMLTextAreaElement;
    const $fullName = $info.querySelector('#full-name') as HTMLInputElement;
    const $company = $info.querySelector('#company') as HTMLInputElement;
    const $email = $info.querySelector('#email') as HTMLInputElement;
    const $referralCode = $info.querySelector('#referral-code') as HTMLInputElement;
    const $phone = $info.querySelector('#phone') as HTMLInputElement;
    const $submitBtn = $info.querySelector('#submit-btn') as HTMLButtonElement;

    const u = new Uploader({
        $input: $uploads.querySelector('input[type="file"]') as HTMLInputElement,
        $dropzones: [$uploads],
        $submitButtons: [$submitBtn],
        $thumbnailsParent: document.querySelector('#uploads-thumbnails') as HTMLElement,
    });

    const p = new Phone($phone);

    let selectedType = '';
    let selectedSize = '';
    let selectedPlating = '';
    const selectedUpgrades: string[] = [];

    // Pre-fill from session
    const sess = await Session().catch(() => undefined);
    if (sess) {
        $fullName.value = sess.user.fullName();
        $company.value = sess.user.company;
        $email.value = sess.user.emailAddress.email;
        await p.ready();
        p.iti.setNumber(sess.user.phoneNumber.phone);
    }

    // Deadline toggle
    for (const $radio of $deadlineRadios) {
        $radio.addEventListener('change', () => {
            const hasDeadline = ($info.querySelector('input[name="deadline-radio"]:checked') as HTMLInputElement).value === 'has-deadline';
            $dateNeededContainer.classList.toggle('d-none', !hasDeadline);
            $dateNeeded.required = hasDeadline;
            if (!hasDeadline) {
                // Hidden, not disabled — a stale past date would block the
                // submit with a message nobody can see.
                $dateNeeded.value = '';
            }
            revalidateDateNeeded();
        });
    }

    // Create a size card
    function createSizeCard(name: string): HTMLElement {
        const $col = document.createElement('div');
        $col.className = 'col-12 col-sm-6 col-md-4 col-lg-3';
        const $card = document.createElement('div');
        $card.className = 'quote-card quote-card-compact';
        $card.role = 'button';
        $card.innerHTML = `<h5 class="quote-card-title">${name}</h5>`;
        $col.appendChild($card);
        return $col;
    }

    // Step 1: Template card click handlers
    $templateCards.forEach($card => {
        $card.addEventListener('click', async () => {
            $templateCards.forEach($c => $c.classList.remove('selected'));
            $card.classList.add('selected');
            selectedType = CARD_TYPES[$card.id] ?? '';

            // Clear previous size step
            $steps.innerHTML = '';
            $info.classList.add('d-none');

            // Load sizes for the selected type
            const sizes = await GetSizes(LINE_ITEM, selectedType);
            const $step = document.createElement('div');
            $step.className = 'quote-step mt-12';
            $step.innerHTML = '<h2 class="text-center mb-4" style="color: #666; font-weight: 300; font-style: italic;">What size are you looking for?</h2>';
            const $grid = document.createElement('div');
            $grid.className = 'row g-4 justify-content-center';
            $step.appendChild($grid);

            for (const size of sizes) {
                if (!size.networkLineItemTypeSize?.enabled) {
                    continue;
                }
                const sizeName = size.networkName();
                const $col = createSizeCard(sizeName);
                const $sizeCard = $col.querySelector('.quote-card') as HTMLElement;

                $sizeCard.addEventListener('click', async () => {
                    $grid.querySelectorAll('.quote-card').forEach($c => $c.classList.remove('selected'));
                    $sizeCard.classList.add('selected');
                    selectedSize = sizeName;
                    $info.classList.add('d-none');

                    // Remove old plating step
                    $steps.querySelector('.step-plating')?.remove();

                    // Load plating options
                    const children = await GetChildren(LINE_ITEM);
                    const plating = children.find(c => c.networkName() === 'Plating');
                    const allowedPlating = ['Antique Copper', 'Antique Gold', 'Antique Silver', 'Black Metal', 'Copper', 'Gold', 'Silver'];
                    const filteredOptions = plating?.childLineItemOptions.filter(o => allowedPlating.includes(o.networkName())) ?? [];

                    if (filteredOptions.length > 0) {
                        const $platingStep = document.createElement('div');
                        $platingStep.className = 'quote-step mt-12 step-plating';
                        $platingStep.innerHTML = '<h2 class="text-center mb-4" style="color: #666; font-weight: 300; font-style: italic;">What plating would you like?</h2>';
                        const $platingGrid = document.createElement('div');
                        $platingGrid.className = 'row g-4 justify-content-center';
                        $platingStep.appendChild($platingGrid);

                        for (const option of filteredOptions) {
                            const optionName = option.networkName();
                            const optionPrice = option.networkChildLineItemOption?.price;
                            const priceLabel = optionPrice?.gt(0) ? `+$${optionPrice.toFixed(2)} per Lapel Pin` : 'Free';
                            const platingFile = PLATING_IMAGES[optionName];
                            const $col = document.createElement('div');
                            $col.className = 'col-12 col-sm-6 col-md-4 col-lg-3';
                            const $card = document.createElement('div');
                            $card.className = 'quote-card';
                            $card.role = 'button';

                            const $platingImg = platingFile ? cloneImageTemplate(platingFile) : null;
                            if ($platingImg) {
                                $card.appendChild($platingImg);
                            }

                            $card.insertAdjacentHTML('beforeend', `
                                <h5 class="quote-card-title">${html.Escape(optionName)}</h5>
                                <p class="quote-card-desc">${html.Escape(priceLabel)}</p>
                            `);
                            $col.appendChild($card);

                            $card.addEventListener('click', () => {
                                $platingGrid.querySelectorAll('.quote-card').forEach($c => $c.classList.remove('selected'));
                                $card.classList.add('selected');
                                selectedPlating = optionName;
                                $info.classList.add('d-none');

                                // Remove old upgrades step
                                $steps.querySelector('.step-upgrades')?.remove();

                                // Show upgrades step (multi-select) — only the 6 shown on the live site
                                const allowedUpgrades = ['Dangler', 'Spinner', 'Blinker', 'Bobble', 'Slider', 'Glitter Color Fill'];
                                const upgradeChildren = children.filter(c => allowedUpgrades.includes(c.networkName()));

                                if (upgradeChildren.length > 0) {
                                    const $upgradeStep = document.createElement('div');
                                    $upgradeStep.className = 'quote-step mt-12 step-upgrades';
                                    $upgradeStep.innerHTML = `
                                        <h2 class="text-center mb-4" style="color: #666; font-weight: 300; font-style: italic;">Would you like any upgrades?</h2>
                                        <p class="text-center text-muted mb-4">Select as many as you'd like, or continue.</p>
                                    `;
                                    const $upgradeGrid = document.createElement('div');
                                    $upgradeGrid.className = 'row g-4 justify-content-center';
                                    $upgradeStep.appendChild($upgradeGrid);

                                    selectedUpgrades.length = 0;

                                    for (const child of upgradeChildren) {
                                        const childName = child.networkName();
                                        const childPrice = child.networkChildLineItem?.price;
                                        const childOptions = child.childLineItemOptions;
                                        let priceStr: string;
                                        if (childPrice?.gt(0)) {
                                            priceStr = `$${childPrice.toFixed(2)} & Up`;
                                        } else if (childOptions.length > 0) {
                                            const lowest = childOptions.reduce((min, o) => {
                                                const p = o.networkChildLineItemOption?.price ?? min;
                                                return p.lt(min) ? p : min;
                                            }, childOptions[0].networkChildLineItemOption?.price ?? Big(0));
                                            priceStr = `$${lowest.toFixed(2)} & Up`;
                                        } else {
                                            priceStr = 'Free';
                                        }
                                        const upgradeFile = UPGRADE_IMAGES[childName];
                                        const $col = document.createElement('div');
                                        $col.className = 'col-12 col-sm-6 col-md-4 col-lg-3';
                                        const $uCard = document.createElement('div');
                                        $uCard.className = 'quote-card';
                                        $uCard.role = 'button';

                                        const $upgradeImg = upgradeFile ? cloneImageTemplate(upgradeFile) : null;
                                        if ($upgradeImg) {
                                            $uCard.appendChild($upgradeImg);
                                        }

                                        $uCard.insertAdjacentHTML('beforeend', `
                                            <h5 class="quote-card-title">${html.Escape(childName)}</h5>
                                            <p class="quote-card-desc">${html.Escape(priceStr)}</p>
                                        `);
                                        $col.appendChild($uCard);

                                        $uCard.addEventListener('click', () => {
                                            $uCard.classList.toggle('selected');
                                            const idx = selectedUpgrades.indexOf(childName);
                                            if (idx >= 0) {
                                                selectedUpgrades.splice(idx, 1);
                                            } else {
                                                selectedUpgrades.push(childName);
                                            }
                                        });

                                        $upgradeGrid.appendChild($col);
                                    }

                                    // Skip button
                                    const $skipRow = document.createElement('div');
                                    $skipRow.className = 'text-center mt-4';
                                    $skipRow.innerHTML = '<button type="button" class="btn-ctp-primary" id="skip-upgrades">Continue</button>';
                                    $upgradeStep.appendChild($skipRow);

                                    $skipRow.querySelector('#skip-upgrades')?.addEventListener('click', () => {
                                        $info.classList.remove('d-none');
                                        $info.scrollIntoView({ behavior: 'smooth' });
                                    });

                                    $steps.appendChild($upgradeStep);
                                    $upgradeStep.scrollIntoView({ behavior: 'smooth' });
                                } else {
                                    $info.classList.remove('d-none');
                                    $info.scrollIntoView({ behavior: 'smooth' });
                                }
                            });

                            $platingGrid.appendChild($col);
                        }

                        $steps.appendChild($platingStep);
                        $platingStep.scrollIntoView({ behavior: 'smooth' });
                    } else {
                        $info.classList.remove('d-none');
                        $info.scrollIntoView({ behavior: 'smooth' });
                    }
                });

                $grid.appendChild($col);
            }

            $steps.appendChild($step);
            $step.scrollIntoView({ behavior: 'smooth' });
        });
    });

    // Form submission
    $form.noValidate = true;
    $form.addEventListener('submit', async e => {
        e.preventDefault();

        revalidateDateNeeded();
        if (!ReportValidity($form)) {
            return;
        }

        if (!selectedType || !selectedSize) {
            ShowError('Please select a product and size');
            return;
        }

        $fieldset.setAttribute('disabled', 'disabled');
        buttons.Start($submitBtn);

        const deadline = ($info.querySelector('input[name="deadline-radio"]:checked') as HTMLInputElement).value;

        // Build children array (plating + selected upgrades)
        const children: models.QuoteRequestCreateChild[] = [];
        if (selectedPlating) {
            children.push(new models.QuoteRequestCreateChild({ child: 'Plating', option: selectedPlating }));
        }
        for (const upgrade of selectedUpgrades) {
            children.push(new models.QuoteRequestCreateChild({ child: upgrade }));
        }

        // Build notes with sport/team colors/upgrades info
        const notesParts: string[] = [];
        if ($sport.value) {
            notesParts.push(`Sport: ${$sport.value}`);
        }
        if ($teamColors.value) {
            notesParts.push(`Team Colors: ${$teamColors.value}`);
        }
        if ($projectNotes.value) {
            notesParts.push($projectNotes.value);
        }

        // Awaited outside the chain — a PUT-failure throw would otherwise skip
        // the catch below and leave the form disabled.
        let uploadIDs: number[];
        try {
            uploadIDs = await u.uploadIDs();
        } catch (err) {
            ShowError(err);
            $fieldset.removeAttribute('disabled');
            buttons.Stop($submitBtn);
            return;
        }

        NewQuoteRequest({
            form: 'free quote',
            name: $fullName.value.trim(),
            company: $company.value,
            phone: await p.format(),
            email: $email.value,
            lineItem: LINE_ITEM,
            type: selectedType,
            size: selectedSize,
            children: children.length > 0 ? children : undefined,
            quantity: parseInt($quantity.value, 10) || 100,
            project: $projectName.value,
            budget: parseFloat($budget.value) || undefined,
            dateNeeded: deadline === 'has-deadline' ? $dateNeeded.value : undefined,
            notes: notesParts.join('\n'),
            promo: $referralCode.value || undefined,
            uploadIDs,
        }).then(() => {
            const items: string[] = [];
            const add = (label: string, value: string) => {
                if (value) {
                    items.push(`<li><strong>${html.Escape(label)}:</strong> ${html.Escape(value)}</li>`);
                }
            };

            add('Name', $fullName.value);
            add('Company', $company.value);
            add('Email', $email.value);
            add('Phone', $phone.value);
            add('Product', selectedType);
            add('Size', selectedSize);
            add('Plating', selectedPlating);
            if (selectedUpgrades.length) {
                items.push(`<li><strong>Upgrades:</strong> ${selectedUpgrades.join(', ')}</li>`);
            }
            add('Quantity', $quantity.value);
            add('Project Name', $projectName.value);
            add('Budget', $budget.value);
            add('Sport', $sport.value);
            add('Team Colors', $teamColors.value);
            add('Notes', $projectNotes.value);

            // Collect uploaded file thumbnails
            const $thumbs = document.querySelector('#uploads-thumbnails');
            const thumbsHtml = $thumbs?.innerHTML ?? '';

            const $card = $info.querySelector('.card-body') as HTMLElement;
            $card.innerHTML = `
                <div class="py-4">
                    <div class="text-center mb-4">
                        <i class="fas fa-check-circle fa-3x text-success mb-3"></i>
                        <h3>Thank you!</h3>
                        <p class="text-muted">We'll get back to you with a quote within 1 business day.</p>
                    </div>
                    <h5 class="text-muted">Your details:</h5>
                    <ul class="list-unstyled text-muted mt-3" style="line-height: 2;">
                        ${items.join('\n')}
                    </ul>
                    ${thumbsHtml ? `<h5 class="text-muted mt-4">Uploaded files:</h5><div class="mt-2 summary-thumbs">${thumbsHtml}</div>` : ''}
                </div>
            `;
            // Remove close buttons from thumbnail summary
            $card.querySelectorAll('.summary-thumbs .upload-thumbnail-close').forEach($el => $el.remove());
        }).catch((err: unknown) => {
            $fieldset.removeAttribute('disabled');
            buttons.Stop($submitBtn);

            // Handle field validation errors from the API (e.g. {"promo": "invalid"})
            const fieldMessages: Record<string, string> = {
                promo: 'Invalid referral code',
            };
            if (err && typeof err === 'object' && 'response' in err) {
                const data = (err as { response?: { data?: Record<string, string> } }).response?.data;
                if (data && typeof data === 'object') {
                    for (const [field, tag] of Object.entries(data)) {
                        const $input = $form.querySelector<HTMLInputElement>(`[name="${field}"]`);
                        if ($input) {
                            const message = fieldMessages[field] ?? tag;
                            $input.setCustomValidity(message);
                            $input.reportValidity();
                            $input.addEventListener('input', () => $input.setCustomValidity(''), { once: true });
                            return;
                        }
                    }
                }
            }

            ShowError(err);
        });
    });
}
